import React, { useState } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  Heart, Gift, Globe, Building2, HandHeart, Sparkles, CheckCircle, Star,
  CreditCard, Building, Smartphone, Loader2, X, ChevronRight, Target, TrendingUp
} from "lucide-react";
import api from "@/lib/api";

const categoryIcons = {
  tithe: Heart,
  offering: Gift,
  missions: Globe,
  building_fund: Building2,
  benevolence: HandHeart,
  thanksgiving: Sparkles,
  pledge: CheckCircle,
  special: Star,
};

const OnlineGiving = ({ unitCode, unitName, onClose }) => {
  const [step, setStep] = useState("category"); // category, amount, details, confirm, success
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedCampaign, setSelectedCampaign] = useState(null);
  const [amount, setAmount] = useState("");
  const [customAmount, setCustomAmount] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState("card");
  const [isAnonymous, setIsAnonymous] = useState(false);
  const [isRecurring, setIsRecurring] = useState(false);
  const [recurringFrequency, setRecurringFrequency] = useState("monthly");
  const [giverInfo, setGiverInfo] = useState({ name: "", email: "", phone: "" });

  // Check for member data
  const memberData = (() => {
    try {
      return JSON.parse(localStorage.getItem("member_data") || "null");
    } catch {
      return null;
    }
  })();

  // Prefill giver info if member is logged in
  React.useEffect(() => {
    if (memberData) {
      setGiverInfo({
        name: memberData.name || "",
        email: memberData.email || "",
        phone: memberData.phone || ""
      });
    }
  }, []);

  // Fetch giving config
  const { data: config, isLoading } = useQuery({
    queryKey: ["giving-config", unitCode],
    queryFn: () => api.get(`/public/org/${unitCode}/giving/config`).then((r) => r.data),
  });

  // Submit giving mutation
  const givingMutation = useMutation({
    mutationFn: (data) => api.post(`/public/org/${unitCode}/giving`, data),
    onSuccess: (response) => {
      setStep("success");
    },
    onError: (err) => {
      toast.error(err.response?.data?.detail || "Failed to process gift");
    },
  });

  const handleSubmit = () => {
    const data = {
      category: selectedCampaign ? "pledge" : selectedCategory,
      amount: parseFloat(amount),
      currency: config?.currency?.code || "USD",
      payment_method: paymentMethod,
      is_recurring: isRecurring,
      recurring_frequency: isRecurring ? recurringFrequency : null,
      campaign_id: selectedCampaign?.id,
      is_anonymous: isAnonymous,
      // Send null instead of empty strings for anonymous giving
      giver_name: isAnonymous ? null : (giverInfo.name || null),
      giver_email: isAnonymous ? null : (giverInfo.email || null),
      giver_phone: isAnonymous ? null : (giverInfo.phone || null),
    };
    givingMutation.mutate(data);
  };

  const suggestedAmounts = selectedCampaign?.suggested_amounts || [25, 50, 100, 250, 500, 1000];
  const currency = config?.currency || { code: "USD", symbol: "$" };

  if (isLoading) {
    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-2xl p-8 flex items-center justify-center">
          <Loader2 className="w-8 h-8 animate-spin text-emerald-600" />
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" data-testid="online-giving-modal">
      <div className="bg-white rounded-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="bg-gradient-to-r from-emerald-600 to-teal-600 px-6 py-4 rounded-t-2xl flex items-center justify-between">
          <div>
            <h2 className="text-xl font-bold text-white">Give Online</h2>
            <p className="text-emerald-100 text-sm">{unitName}</p>
          </div>
          <button onClick={onClose} className="text-white/80 hover:text-white" data-testid="close-giving-modal">
            <X className="w-6 h-6" />
          </button>
        </div>

        <div className="p-6">
          {/* Step: Category Selection */}
          {step === "category" && (
            <div className="space-y-4">
              <p className="text-slate-600 mb-4">What would you like to give towards?</p>

              {/* Active Campaigns */}
              {config?.campaigns?.length > 0 && (
                <div className="mb-6">
                  <h3 className="font-medium text-slate-900 mb-3">Active Campaigns</h3>
                  <div className="space-y-3">
                    {config.campaigns.map((campaign) => (
                      <button
                        key={campaign.id}
                        onClick={() => {
                          setSelectedCampaign(campaign);
                          setStep("amount");
                        }}
                        className="w-full p-4 border-2 border-emerald-200 rounded-xl hover:border-emerald-500 hover:bg-emerald-50 transition-all text-left"
                      >
                        <div className="flex items-center justify-between mb-2">
                          <span className="font-medium text-slate-900">{campaign.name}</span>
                          <Target className="w-5 h-5 text-emerald-600" />
                        </div>
                        {campaign.description && (
                          <p className="text-sm text-slate-500 mb-3">{campaign.description}</p>
                        )}
                        <div className="relative h-2 bg-slate-200 rounded-full overflow-hidden">
                          <div
                            className="absolute inset-y-0 left-0 bg-emerald-500 rounded-full transition-all"
                            style={{ width: `${campaign.progress_percent || 0}%` }}
                          />
                        </div>
                        <div className="flex justify-between mt-2 text-sm">
                          <span className="text-emerald-600 font-medium">
                            {currency.symbol}{campaign.current_amount?.toLocaleString() || 0}
                          </span>
                          <span className="text-slate-500">
                            of {currency.symbol}{campaign.target_amount?.toLocaleString()} goal
                          </span>
                        </div>
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Regular Categories */}
              <h3 className="font-medium text-slate-900 mb-3">Categories</h3>
              <div className="grid grid-cols-2 gap-3">
                {config?.categories?.map((cat) => {
                  const Icon = categoryIcons[cat.code] || Heart;
                  return (
                    <button
                      key={cat.code}
                      onClick={() => {
                        setSelectedCategory(cat.code);
                        setStep("amount");
                      }}
                      className="p-4 border rounded-xl hover:border-emerald-500 hover:bg-emerald-50 transition-all text-left group"
                    >
                      <Icon className="w-6 h-6 text-emerald-600 mb-2 group-hover:scale-110 transition-transform" />
                      <p className="font-medium text-slate-900">{cat.name}</p>
                      <p className="text-xs text-slate-500">{cat.description}</p>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* Step: Amount Selection */}
          {step === "amount" && (
            <div className="space-y-4">
              <button
                onClick={() => {
                  setStep("category");
                  setSelectedCampaign(null);
                }}
                className="text-slate-500 hover:text-slate-700 text-sm flex items-center gap-1"
              >
                ← Back
              </button>

              <div className="text-center mb-6">
                <p className="text-slate-600">
                  {selectedCampaign ? selectedCampaign.name : selectedCategory?.replace(/_/g, " ")}
                </p>
                <p className="text-3xl font-bold text-slate-900 mt-2">
                  {currency.symbol}{amount || "0"}
                </p>
              </div>

              {/* Suggested Amounts */}
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                {suggestedAmounts.map((amt) => (
                  <button
                    key={amt}
                    onClick={() => {
                      setAmount(amt.toString());
                      setCustomAmount(false);
                    }}
                    className={`py-3 rounded-lg font-medium transition-all ${
                      amount === amt.toString() && !customAmount
                        ? "bg-emerald-600 text-white"
                        : "bg-slate-100 text-slate-700 hover:bg-slate-200"
                    }`}
                  >
                    {currency.symbol}{amt}
                  </button>
                ))}
              </div>

              {/* Custom Amount */}
              <div>
                <label className="block text-sm text-slate-600 mb-1">Custom Amount</label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400">
                    {currency.symbol}
                  </span>
                  <input
                    type="number"
                    value={customAmount ? amount : ""}
                    onChange={(e) => {
                      setAmount(e.target.value);
                      setCustomAmount(true);
                    }}
                    placeholder="Enter amount"
                    className="w-full pl-8 pr-4 py-3 border rounded-lg focus:ring-2 focus:ring-emerald-500"
                  />
                </div>
              </div>

              {/* Recurring Option */}
              <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
                <div>
                  <p className="font-medium text-slate-900">Make this recurring?</p>
                  <p className="text-sm text-slate-500">Support regularly</p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={isRecurring}
                    onChange={(e) => setIsRecurring(e.target.checked)}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-slate-200 peer-focus:ring-4 peer-focus:ring-emerald-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-slate-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-emerald-600"></div>
                </label>
              </div>

              {isRecurring && (
                <select
                  value={recurringFrequency}
                  onChange={(e) => setRecurringFrequency(e.target.value)}
                  className="w-full px-4 py-3 border rounded-lg"
                >
                  <option value="weekly">Weekly</option>
                  <option value="monthly">Monthly</option>
                  <option value="quarterly">Quarterly</option>
                </select>
              )}

              <button
                onClick={() => setStep("details")}
                disabled={!amount || parseFloat(amount) <= 0}
                className="w-full py-3 bg-emerald-600 text-white rounded-lg font-medium hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                Continue
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          )}

          {/* Step: Details */}
          {step === "details" && (
            <div className="space-y-4">
              <button
                onClick={() => setStep("amount")}
                className="text-slate-500 hover:text-slate-700 text-sm flex items-center gap-1"
              >
                ← Back
              </button>

              <div className="bg-emerald-50 rounded-lg p-4 mb-4">
                <div className="flex justify-between">
                  <span className="text-slate-600">Amount</span>
                  <span className="font-bold text-emerald-700">
                    {currency.symbol}{parseFloat(amount).toLocaleString()}
                    {isRecurring && ` / ${recurringFrequency}`}
                  </span>
                </div>
              </div>

              {/* Payment Method */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Payment Method</label>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                  <button
                    onClick={() => setPaymentMethod("card")}
                    className={`p-3 rounded-lg border-2 flex flex-col items-center gap-1 transition-all ${
                      paymentMethod === "card" ? "border-emerald-500 bg-emerald-50" : "border-slate-200"
                    }`}
                  >
                    <CreditCard className="w-5 h-5" />
                    <span className="text-xs">Card</span>
                  </button>
                  <button
                    onClick={() => setPaymentMethod("bank_transfer")}
                    className={`p-3 rounded-lg border-2 flex flex-col items-center gap-1 transition-all ${
                      paymentMethod === "bank_transfer" ? "border-emerald-500 bg-emerald-50" : "border-slate-200"
                    }`}
                  >
                    <Building className="w-5 h-5" />
                    <span className="text-xs">Bank</span>
                  </button>
                  <button
                    onClick={() => setPaymentMethod("mobile_money")}
                    className={`p-3 rounded-lg border-2 flex flex-col items-center gap-1 transition-all ${
                      paymentMethod === "mobile_money" ? "border-emerald-500 bg-emerald-50" : "border-slate-200"
                    }`}
                  >
                    <Smartphone className="w-5 h-5" />
                    <span className="text-xs">Mobile</span>
                  </button>
                </div>
              </div>

              {/* Giver Info */}
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <label className="block text-sm font-medium text-slate-700">Your Information</label>
                  <label className="flex items-center gap-2 text-sm text-slate-600">
                    <input
                      type="checkbox"
                      checked={isAnonymous}
                      onChange={(e) => setIsAnonymous(e.target.checked)}
                      className="rounded border-slate-300"
                    />
                    Give Anonymously
                  </label>
                </div>

                {!isAnonymous && (
                  <>
                    <input
                      type="text"
                      placeholder="Your Name"
                      value={giverInfo.name}
                      onChange={(e) => setGiverInfo({ ...giverInfo, name: e.target.value })}
                      className="w-full px-4 py-2 border rounded-lg"
                    />
                    <input
                      type="email"
                      placeholder="Email (for receipt)"
                      value={giverInfo.email}
                      onChange={(e) => setGiverInfo({ ...giverInfo, email: e.target.value })}
                      className="w-full px-4 py-2 border rounded-lg"
                    />
                    <input
                      type="tel"
                      placeholder="Phone (optional)"
                      value={giverInfo.phone}
                      onChange={(e) => setGiverInfo({ ...giverInfo, phone: e.target.value })}
                      className="w-full px-4 py-2 border rounded-lg"
                    />
                  </>
                )}
              </div>

              <button
                onClick={handleSubmit}
                disabled={givingMutation.isPending || (!isAnonymous && !giverInfo.email)}
                className="w-full py-3 bg-emerald-600 text-white rounded-lg font-medium hover:bg-emerald-700 disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {givingMutation.isPending ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  <>
                    Complete Gift
                    <Heart className="w-4 h-4" />
                  </>
                )}
              </button>
            </div>
          )}

          {/* Step: Success */}
          {step === "success" && (
            <div className="text-center py-8">
              <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="w-8 h-8 text-emerald-600" />
              </div>
              <h3 className="text-xl font-bold text-slate-900 mb-2">Thank You!</h3>
              <p className="text-slate-600 mb-4">
                Your gift of {currency.symbol}{parseFloat(amount).toLocaleString()} has been
                recorded and is pending — it is confirmed once the payment reaches us.
              </p>
              {!isAnonymous && giverInfo.email && (
                <p className="text-sm text-slate-500 mb-6">
                  Your receipt goes to {giverInfo.email} as soon as it is confirmed
                </p>
              )}
              <button
                onClick={onClose}
                className="px-6 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700"
              >
                Done
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default OnlineGiving;
